<mat-table
  [dataSource]="dataSource()"
  [matSortDisabled]="!sortable()"
  matSort
  class="w-full min-w-[800px]"
>
  <!-- SELECT column -->
  <ng-container [matColumnDef]="TableColumnsEnum.SELECT">
    <mat-header-cell *matHeaderCellDef>
      @if (showSelectAll()) {
        <mat-checkbox
          (change)="onSelectAll()"
          [checked]="isAllSelected()"
          [disabled]="selectionDisabled() || !allSelectableIds().length"
          [indeterminate]="isIndeterminate()"
        />
      }
    </mat-header-cell>
    <mat-cell *matCellDef="let row">
      <mat-checkbox
        (change)="onSelectReport(row)"
        [checked]="isSelected(row)"
        [disabled]="isSelectDisabled(row)"
      />
    </mat-cell>
  </ng-container>

  <!-- DATE column -->
  <ng-container [matColumnDef]="TableColumnsEnum.DATE">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by date"
    >
      {{ 'reports-work-time-report-table-desktop-view-date' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      class="whitespace-nowrap"
    >
      <div class="flex flex-col leading-tight">
        <span>{{ row.date | proffeoDate: 'd MMM' }}</span>
        <span>{{ row.date | proffeoDate: 'yyyy' }}</span>
        @if (showInlineStatus()) {
          <proffeo-ui-report-status-badge
            [status]="row.status"
            variant="label"
          />
        }
      </div>
    </mat-cell>
  </ng-container>

  <!-- USER column: reporter (`userName`), not createdBy -->
  <ng-container [matColumnDef]="TableColumnsEnum.USER">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by user"
    >
      {{ 'reports-work-time-report-table-desktop-view-user' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="row.userName"
      class="min-w-0"
    >
      <span class="min-w-0 break-words">{{ row.userName }}</span>
    </mat-cell>
  </ng-container>

  <!-- COMPANY column -->
  <ng-container [matColumnDef]="TableColumnsEnum.COMPANY">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by company"
    >
      {{ 'reports-work-time-report-table-desktop-view-company' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="row.companyName"
    >
      {{ row.companyName ?? '-' }}
    </mat-cell>
  </ng-container>

  <!-- PROJECT column -->
  <ng-container [matColumnDef]="TableColumnsEnum.PROJECT">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by project"
    >
      {{ 'reports-work-time-report-table-desktop-view-project' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      class="min-w-0"
    >
      <div class="flex min-w-0 flex-col leading-tight">
        <span class="break-words">{{ row.projectName ?? '-' }}</span>
        @if (showMobileProjectTask() && row.task?.name) {
          <span class="proffeo-ui-text-muted break-words">{{ row.task?.name }}</span>
        }
      </div>
    </mat-cell>
  </ng-container>

  <!-- TASK_ID column -->
  <ng-container [matColumnDef]="TableColumnsEnum.TASK_ID">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by task ID"
    >
      {{ 'reports-work-time-report-table-desktop-view-task-id' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="
        $safeNavigationMigration(row.task?.taskTypeCode) + '-' + $safeNavigationMigration(row.task?.taskNumber)
      "
    >
      @if (row.task?.taskTypeCode && row.task?.taskNumber) {
        <div
          (click)="openTaskDetails($event, $safeNavigationMigration(row.task?.id))"
          class="cursor-pointer text-nowrap rounded-md border border-proffeo-overlay-subtle bg-proffeo-surface-muted px-2 py-0.5 text-sm inline-block"
        >
          {{ row.task?.taskTypeCode + '-' + row.task?.taskNumber }}
        </div>
      } @else {
        <span>-</span>
      }
    </mat-cell>
  </ng-container>

  <!-- TASK_TITLE column -->
  <ng-container [matColumnDef]="TableColumnsEnum.TASK_TITLE">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by task title"
    >
      {{ 'reports-work-time-report-table-desktop-view-task' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="$safeNavigationMigration(row.task?.name)"
      class="max-w-[200px]"
    >
      <div
        (click)="openTaskDetails($event, $safeNavigationMigration(row.task?.id))"
        class="truncate cursor-pointer"
      >
        {{ row.task?.name ?? '-' }}
      </div>
    </mat-cell>
  </ng-container>

  <!-- LOCATION column -->
  <ng-container [matColumnDef]="TableColumnsEnum.LOCATION">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by location"
    >
      {{ 'reports-work-time-report-table-desktop-view-location' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="row.locationName"
    >
      {{ row.locationName ?? '-' }}
    </mat-cell>
  </ng-container>

  <!-- DESCRIPTION column -->
  <ng-container [matColumnDef]="TableColumnsEnum.DESCRIPTION">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by description"
    >
      {{ 'reports-work-time-report-table-desktop-view-description' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="row.description"
      class="max-w-48"
    >
      <div class="truncate">{{ row.description ?? '-' }}</div>
    </mat-cell>
  </ng-container>

  <!-- TYPE column -->
  <ng-container [matColumnDef]="TableColumnsEnum.TYPE">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by type"
    >
      {{ 'reports-work-time-report-table-desktop-view-type' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="row.typeTranslationKey ? (row.typeTranslationKey | translate) : ''"
      class="min-w-0"
    >
      <div class="flex min-w-0 flex-col gap-1">
        <div class="flex min-w-0 items-center gap-1">
          <span class="break-words">
            @if (row.typeTranslationKey) {
              {{ row.typeTranslationKey | translate }}
            } @else {
              -
            }
          </span>
          @if (showCompactCommentIndicator() && row.comment) {
            <mat-icon
              [attr.aria-label]="'time-report-comment' | translate"
              [matTooltip]="'time-report-comment' | translate"
              fontIcon="comment"
              class="icon-muted"
            />
          }
        </div>
        @if (showStatusInType()) {
          <proffeo-ui-report-status-badge
            [status]="row.status"
            variant="label"
          />
        }
      </div>
    </mat-cell>
  </ng-container>

  <!-- COMMENT column -->
  <ng-container [matColumnDef]="TableColumnsEnum.COMMENT">
    <mat-header-cell *matHeaderCellDef>
      {{ 'time-report-comment' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      [matTooltip]="row.comment"
      class="max-w-48"
    >
      <div class="truncate">{{ row.comment ?? '-' }}</div>
    </mat-cell>
  </ng-container>

  <!-- ESTIMATION column -->
  <ng-container [matColumnDef]="TableColumnsEnum.ESTIMATION">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by estimation"
    >
      {{ 'reports-work-time-report-table-desktop-view-estimation' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      class="text-right"
    >
      @if (row.estimation) {
        {{ row.estimation | hmDuration }}
      } @else {
        -
      }
    </mat-cell>
  </ng-container>

  <!-- TOTAL column -->
  <ng-container [matColumnDef]="TableColumnsEnum.TOTAL">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      sortActionDescription="Sort by total"
    >
      {{ 'reports-work-time-report-table-desktop-view-total' | translate }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let row"
      class="whitespace-nowrap"
    >
      {{ row.duration | hmDuration }}
    </mat-cell>
  </ng-container>

  <!-- STATUS column -->
  <ng-container [matColumnDef]="TableColumnsEnum.STATUS">
    <mat-header-cell *matHeaderCellDef>
      {{ 'reports-work-time-report-table-desktop-view-status' | translate }}
    </mat-header-cell>
    <mat-cell *matCellDef="let row">
      <div class="flex justify-center">
        <proffeo-ui-report-status-badge [status]="row.status" />
      </div>
    </mat-cell>
  </ng-container>

  <!-- ACTIONS column -->
  <ng-container [matColumnDef]="TableColumnsEnum.ACTIONS">
    <mat-header-cell *matHeaderCellDef></mat-header-cell>
    <mat-cell *matCellDef="let row">
      <mat-icon
        [matMenuTriggerFor]="reportActions"
        fontIcon="more_vert"
        click
        event
        stopPropagation
        class="cursor-pointer hover:icon-default break-normal icon-muted"
      />
      <mat-menu #reportActions="matMenu">
        <button
          (click)="onEditReport(row)"
          mat-menu-item
        >
          <mat-icon fontIcon="edit" />
          <span>{{ 'time-reports.actions.edit' | translate }}</span>
        </button>
        <button
          (click)="onDeleteReport(row)"
          mat-menu-item
        >
          <mat-icon fontIcon="delete" />
          <span>{{ 'time-reports.actions.delete' | translate }}</span>
        </button>
      </mat-menu>
    </mat-cell>
  </ng-container>

  <mat-header-row *matHeaderRowDef="columns()"></mat-header-row>
  <mat-row
    *matRowDef="let row; columns: columns()"
    [class.bg-amber-50]="row.reportType === 'Absence'"
  ></mat-row>
  <div
    *matNoDataRow
    class="text-center py-5"
  >
    <p class="text-proffeo-text">{{ 'placeholders.no-results-found' | translate }}</p>
  </div>
</mat-table>
